import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';
import { InfoTooltip } from '../Common/InfoTooltip';
import '../../styles/LoyaltyPrograms.css';

function LoyaltyPrograms() {
  const [programs, setPrograms] = useState([]);
  const [newProgram, setNewProgram] = useState({ 
    name: '', 
    description: '', 
    stamps_required: '', 
    about: '' 
  });
  const [qrCodes, setQRCodes] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editProgramData, setEditProgramData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    message: '',
    onConfirm: null,
    onCancel: null
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    if (!isLoading && programs.length > 0) {
      // Find all programs with QR check-in enabled but no QR code loaded yet
      const programsNeedingQR = programs.filter(
        program => program.allow_qr_checkin && !qrCodes[program.id]
      );
      
      // Generate QR codes for all these programs
      programsNeedingQR.forEach(program => {
        generateQRCode(program.id);
      });
    }
  }, [isLoading, programs]);

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const fetchPrograms = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/vendors/dashboard-programs');
      setPrograms(response.data.map(program => ({
        ...program,
        allow_qr_checkin: program.allow_qr_checkin || false
      })));
    } catch (error) {
      console.error('Error fetching programs:', error);
      setToast({
        type: 'error',
        message: 'Failed to load loyalty programs'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (data) => {
    const errors = {};
    if (!data.name || data.name.trim() === '') {
      errors.name = 'Program name is required';
    }
    if (!data.description || data.description.trim() === '') {
      errors.description = 'Reward description is required';
    }
    if (!data.stamps_required || isNaN(data.stamps_required) || data.stamps_required < 1) {
      errors.stamps_required = 'Valid number of stamps is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (isEditing) {
      setEditProgramData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setNewProgram(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    
    if (!validateForm(newProgram)) {
      return;
    }

    setIsSaving(true);
    try {
      await axios.post('/api/vendors/programs', newProgram);
      setNewProgram({ name: '', description: '', stamps_required: '', about: '' });
      fetchPrograms();
      setIsCreating(false);
      setToast({
        type: 'success',
        message: 'Loyalty program created successfully'
      });
    } catch (error) {
      console.error('Error creating program:', error);
      setToast({
        type: 'error',
        message: 'Failed to create loyalty program'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateProgram = async (e) => {
    e.preventDefault();
    
    if (!validateForm(editProgramData)) {
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`/api/vendors/programs/${editProgramData.id}`, editProgramData);
      setPrograms(programs.map(p => p.id === editProgramData.id ? editProgramData : p));
      setIsEditing(false);
      setEditProgramData(null);
      setToast({
        type: 'success',
        message: 'Loyalty program updated successfully'
      });
    } catch (error) {
      console.error('Error updating program:', error);
      setToast({
        type: 'error',
        message: 'Failed to update loyalty program'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProgram = async (programId) => {
    setConfirmDialog({
      show: true,
      message: 'Are you sure you want to delete this loyalty program? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await axios.delete(`/api/vendors/programs/${programId}`);
          setPrograms(prev => prev.filter(p => p.id !== programId));
          setToast({
            type: 'success',
            message: 'Loyalty program deleted successfully'
          });
        } catch (error) {
          console.error('Error deleting program:', error);
          setToast({
            type: 'error',
            message: 'Failed to delete loyalty program'
          });
        }
        setConfirmDialog({ show: false });
      },
      onCancel: () => {
        setConfirmDialog({ show: false });
      }
    });
  };

  const toggleProgramActive = async (programId) => {
    try {
      await axios.put(`/api/vendors/programs/${programId}/toggle-active`);
      setPrograms(programs.map(program => 
        program.id === programId ? {...program, active: !program.active} : program
      ));
      setToast({
        type: 'success',
        message: 'Program status updated'
      });
    } catch (error) {
      console.error('Error toggling program active status:', error);
      setToast({
        type: 'error',
        message: 'Failed to update program status'
      });
    }
  };


  const toggleQRCheckin = async (programId) => {
    try {
      const response = await axios.put(`/api/vendors/programs/${programId}/toggle-qr-checkin`);
      const updatedAllowQRCheckin = response.data.allow_qr_checkin;
      
      setPrograms(programs.map(program => 
        program.id === programId ? {...program, allow_qr_checkin: updatedAllowQRCheckin} : program
      ));
      
      // Automatically generate QR code when enabled
      if (updatedAllowQRCheckin) {
        generateQRCode(programId);
      }
      
      setToast({
        type: 'success',
        message: updatedAllowQRCheckin ? 'QR check-in enabled' : 'QR check-in disabled'
      });
    } catch (error) {
      console.error('Error toggling QR check-in:', error);
      setToast({
        type: 'error',
        message: 'Failed to update QR check-in setting'
      });
    }
  };

  const generateQRCode = async (programId) => {
    try {
      const response = await axios.get(`/api/vendors/programs/${programId}/qr-code`);
      setQRCodes(prevCodes => ({...prevCodes, [programId]: response.data.qrCode}));
    } catch (error) {
      console.error('Error generating QR code:', error);
      setToast({
        type: 'error',
        message: 'Failed to generate QR code'
      });
    }
  };



  const startEditing = (program) => {
    setEditProgramData({...program});
    setIsEditing(true);
    setIsCreating(false);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditProgramData(null);
    setFormErrors({});
  };

  const handleStampAmountChange = (e) => {
    const { name, value } = e.target;
    
    if (isEditing && editProgramData.stamps_required !== value && programs.some(p => p.id === editProgramData.id)) {
      setConfirmDialog({
        show: true,
        message: "Changing the number of stamps required will affect both new and existing customers. Are you sure you want to continue?",
        onConfirm: () => {
          handleInputChange({
            target: { name, value }
          });
          setConfirmDialog({ show: false });
        },
        onCancel: () => {
          // Reset to the previous value
          e.target.value = editProgramData.stamps_required;
          setConfirmDialog({ show: false });
        }
      });
    } else {
      handleInputChange(e);
    }
  };

  const handleMenuToggle = (e, programId) => {
    e.stopPropagation();
    e.preventDefault(); // Add this line
    const currentlyOpen = openMenuId === programId;
    setOpenMenuId(currentlyOpen ? null : programId);
  };

  if (isLoading) {
    return <div className="lp-loading">Loading loyalty programs...</div>;
  }

  return (
    <div className="lp-container">
      {toast && (
        <div className={`lp-toast ${toast.type}`}>
          <div className="lp-toast-content">
            <span className={`lp-toast-icon ${toast.type}`}>
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="lp-toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="lp-header">
        <div className="lp-header-content">
          <h1>Loyalty Programs</h1>
          <p className="lp-header-subtitle">Create and manage customer loyalty programs</p>
        </div>
      </div>

      {programs.length === 0 && !isCreating ? (
        <div className="lp-empty-state">
          <div className="lp-empty-icon">🎁</div>
          <h3>No loyalty programs yet</h3>
          <p>Create your first loyalty program to start rewarding your customers</p>
          <button 
            type="button" 
            onClick={() => {
              setIsCreating(true);
              setIsEditing(false);
              setEditProgramData(null);
            }} 
            className="lp-button primary"
          >
            Create First Program
          </button>
        </div>
      ) : (
        <>
          {!isCreating && !isEditing && (
            <div className="lp-actions">
              <button 
                type="button" 
                onClick={() => {
                  setIsCreating(true);
                  setIsEditing(false);
                  setEditProgramData(null);
                }} 
                className="lp-button primary"
              >
                Create New Program
              </button>
            </div>
          )}

          {(isCreating || isEditing) && (
            <div className="lp-card">
              <div className="lp-card-header">
                <h2>{isEditing ? 'Edit Loyalty Program' : 'Create New Loyalty Program'}</h2>
                <button 
                  type="button" 
                  onClick={() => {
                    setIsCreating(false);
                    setIsEditing(false);
                    setEditProgramData(null);
                    setFormErrors({});
                  }} 
                  className="lp-button-icon"
                  aria-label="Close form"
                >
                  ×
                </button>
              </div>
              <div className="lp-card-body">
                <form onSubmit={isEditing ? handleUpdateProgram : handleCreateProgram} className="lp-form">
                  <div className="lp-form-grid">
                    <div className={`lp-form-group ${formErrors.name ? 'error' : ''}`}>
                      <label className="lp-label" htmlFor="program-name">
                        Program Name <span className="lp-required">*</span>
                      </label>
                      <input
                        type="text"
                        id="program-name"
                        name="name"
                        value={isEditing ? editProgramData.name : newProgram.name}
                        onChange={handleInputChange}
                        className="lp-input"
                        placeholder="e.g., Coffee Lovers Club"
                      />
                      {formErrors.name && (
                        <span className="lp-error-message">{formErrors.name}</span>
                      )}
                    </div>

                    <div className={`lp-form-group ${formErrors.stamps_required ? 'error' : ''}`}>
                        <label className="lp-label" htmlFor="program-stamps">
                            Stamps Required <span className="lp-required">*</span>
                            <InfoTooltip 
                            text="Number of stamps a customer needs to collect to earn the reward" 
                            variant="vendor"
                            />
                        </label>
                        <input
                            type="number"
                            id="program-stamps"
                            name="stamps_required"
                            min="1"
                            value={isEditing ? editProgramData.stamps_required : newProgram.stamps_required}
                            onChange={handleStampAmountChange}
                            className="lp-input"
                            placeholder="e.g., 10"
                        />
                        {isEditing && (
                            <p className="lp-warning-text">
                            Note: Changing this value will affect both new and existing customer loyalty cards
                            </p>
                        )}
                        {formErrors.stamps_required && (
                            <span className="lp-error-message">{formErrors.stamps_required}</span>
                        )}
                        </div>

                    <div className={`lp-form-group lp-full-width ${formErrors.description ? 'error' : ''}`}>
                      <label className="lp-label" htmlFor="program-reward">
                        Reward Description <span className="lp-required">*</span>
                        <InfoTooltip 
                          text="What customers will receive when they complete their card" 
                          variant="vendor"
                        />
                      </label>
                      <input
                        type="text"
                        id="program-reward"
                        name="description"
                        value={isEditing ? editProgramData.description : newProgram.description}
                        onChange={handleInputChange}
                        className="lp-input"
                        placeholder="e.g., Free coffee of your choice"
                      />
                      {formErrors.description && (
                        <span className="lp-error-message">{formErrors.description}</span>
                      )}
                    </div>

                    <div className="lp-form-group lp-full-width">
                      <label className="lp-label" htmlFor="program-about">
                        Program Details
                        <InfoTooltip 
                          text="Additional information about your program that will be shown to customers" 
                          variant="vendor"
                        />
                      </label>
                      <textarea
                        id="program-about"
                        name="about"
                        value={isEditing ? editProgramData.about : newProgram.about}
                        onChange={handleInputChange}
                        className="lp-input lp-textarea"
                        placeholder="Enter details about your loyalty program (optional)"
                        rows="5"
                        maxLength="1000"
                      ></textarea>
                      <span className="lp-character-count">
                        {(isEditing ? editProgramData.about?.length || 0 : newProgram.about?.length || 0)}/1000 characters
                      </span>
                    </div>
                  </div>

                  <div className="lp-form-actions">
                    <button 
                      type="button" 
                      onClick={() => {
                        setIsCreating(false);
                        setIsEditing(false);
                        setEditProgramData(null);
                        setFormErrors({});
                      }} 
                      className="lp-button secondary"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="lp-button primary"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <span className="lp-spinner"></span> 
                          {isEditing ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        isEditing ? 'Update Program' : 'Create Program'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {!isCreating && !isEditing && programs.length > 0 && (
            <div className="lp-programs-grid">
              {programs.map(program => (
                <div key={program.id} className={`lp-program-card ${!program.active ? 'inactive' : ''}`}>
                  <div className="lp-program-status">
                    <span className={`lp-status-badge ${program.active ? 'active' : 'inactive'}`}>
                      {program.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                
                  <div className="lp-program-header">
                <h3>{program.name}</h3>
                <div className="lp-program-action-buttons">
                    <button 
                    type="button" 
                    onClick={() => startEditing(program)} 
                    className="lp-button secondary small"
                    >
                    Edit
                    </button>
                    <button 
                    type="button" 
                    onClick={() => toggleProgramActive(program.id)} 
                    className="lp-button secondary small"
                    >
                    {program.active ? 'Deactivate' : 'Activate'}
                    </button>
                    {/* <div className={`lp-menu-dropdown ${openMenuId === program.id ? 'open' : ''}`}>
  <button 
    className="lp-menu-button" 
    onClick={(e) => handleMenuToggle(e, program.id)} 
    aria-label="Program options"
  >
    •••
  </button>
  <div className="lp-menu-content">
    <button 
      type="button" 
      onClick={(e) => {
        e.stopPropagation(); // Stop click from closing menu immediately
        handleDeleteProgram(program.id);
      }}
      className="lp-menu-item danger"
    >
      Delete
    </button>
  </div>
</div> */}
                </div>
                </div>
                  
          
                    <div className="lp-program-details">
                    <div className="lp-detail-item">
                        <label>Stamps Required:</label>
                        <span>{program.stamps_required}</span>
                    </div>
                    {/* <div className="lp-detail-item">
                        <label>Reward:</label>
                        <span>{program.description}</span>
                    </div>
                    {program.about && (
                        <div className="lp-detail-item">
                        <label>Additional Details:</label>
                        <p className="lp-detail-text">{program.about}</p>
                        </div>
                    )} */}
                    
                    <div className="lp-program-card-preview">
                    <div className="lp-card-header-preview">
                        <h4>{program.name}</h4>
                        <div className="lp-card-logo-placeholder">
                        CUSTOMER LOYALTY CARD
                        </div>
                    </div>
                    
                    <div className="lp-card-content-preview">
                        <p className="lp-card-reward-preview">{program.description}</p>
                        
                        <div className="lp-stamps-grid-preview">
                        {Array.from({ length: Math.min(program.stamps_required, 10) }).map((_, i) => (
                            <div key={i} className="lp-stamp-circle-preview" />
                        ))}
                        </div>
                        
                        {program.about && (
                        <p className="lp-card-details-preview">{program.about}</p>
                        )}
                    </div>
                    </div>

                    </div>

               
                    <div className="lp-program-qr">
                    <div className="lp-qr-toggle">
                        <label className="lp-switch">
                        <input 
                            type="checkbox" 
                            checked={program.allow_qr_checkin} 
                            onChange={() => toggleQRCheckin(program.id)}
                        />
                        <span className="lp-slider"></span>
                        </label>
                        <span>Customer QR Check-in</span>
                        <InfoTooltip 
                        text="Allow customers to check in using this QR code that you display at your business" 
                        variant="vendor"
                        />
                    </div>
                    
                    {program.allow_qr_checkin && (
                        <div className="lp-qr-code-section">
                        <div className="lp-qr-explanation">
                            Display this QR code at your business for customers to scan and earn stamps
                        </div>
                        {qrCodes[program.id] ? (
                            <div className="lp-qr-display">
                            <img src={qrCodes[program.id]} alt="QR Code" className="lp-qr-image" />
                            <a href={qrCodes[program.id]} download={`qr-code-${program.name}.png`} className="lp-button secondary small">
                                Download QR
                            </a>
                            </div>
                        ) : (
                            <div className="lp-qr-loading">
                            <span className="lp-spinner"></span> Generating QR code...
                            </div>
                        )}
                        </div>
                    )}
                    </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {confirmDialog.show && (
  <div className="lp-confirm-dialog-overlay">
    <div className="lp-confirm-dialog">
      <p>{confirmDialog.message}</p>
      <div className="lp-confirm-actions">
        <button 
          className="lp-button secondary" 
          onClick={confirmDialog.onCancel}
        >
          Cancel
        </button>
        <button 
          className="lp-button primary" 
          onClick={confirmDialog.onConfirm}
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}

export default LoyaltyPrograms;